import { Link } from "react-router-dom";
import { ArrowRight, Eye, Brain, Gauge, Users, Cpu, Building, MapPin, Mail, Phone } from "lucide-react";
import { motion } from "framer-motion";

const pillars = [
  { title: "Sense & Understand", description: "Turn operational data and observations into a useful picture", icon: Eye },
  { title: "Model & Evaluate", description: "Test scenarios and quantify uncertainty before committing", icon: Brain },
  { title: "Decide & Optimise", description: "Support decisions by comparing options and consequences", icon: Gauge },
  { title: "Coordinate & Control", description: "Bring assets, information and operators into governed workflows", icon: Users },
  { title: "Autonomy & Integration", description: "Apply bounded autonomous functions to compatible platforms", icon: Cpu },
];

export default function CompanyPage() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-caption mb-4">About</p>
            <h1 className="text-display text-foreground mb-6">
              The RLK Group
            </h1>
            <p className="text-subtitle max-w-2xl">
              Intelligence and autonomy technologies for organisations that need to understand difficult environments and act with greater control.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Identity */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <h2 className="text-headline text-foreground mb-6">
              What We Do
            </h2>
            <p className="text-body mb-4">
              The RLK Group develops operational intelligence, mission software and autonomy technologies for defence, public safety and critical infrastructure.
            </p>
            <p className="text-body mb-4">
              From simulation and evidence to multi-asset coordination and platform-level autonomy, we help organisations make better decisions in complex operational environments.
            </p>
            <p className="text-body">
              Our approach starts with the operational requirement—not with a product catalogue. We design systems that solve defined problems with traceable evidence, integrating existing infrastructure and qualified partner equipment where appropriate.
            </p>
          </div>
        </div>
      </section>

      {/* Five Pillars */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Capabilities</p>
            <h2 className="text-headline text-foreground">Five Capability Pillars</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                className="card-premium p-5 text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <pillar.icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <h3 className="text-sm font-medium text-foreground mb-1">{pillar.title}</h3>
                <p className="text-xs text-muted-foreground">{pillar.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Operating Approach */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-5xl mx-auto">
            <div>
              <h2 className="text-headline text-foreground mb-6">
                How We Work
              </h2>
              <p className="text-body mb-4">
                We design systems around operational requirements, not around product catalogues. Our approach integrates existing infrastructure, qualified partner equipment, and RLK technology to solve defined problems.
              </p>
              <p className="text-body">
                Every engagement starts with understanding what you need to achieve. We then define the smallest credible system to achieve it—with traceable evidence and clear boundaries.
              </p>
            </div>
            <div className="card-pillar p-6">
              <h3 className="text-label text-foreground mb-4">Operating Principles</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Start with the operational requirement</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Retain existing infrastructure where practical</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Integrate partner equipment through governed interfaces</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Apply RLK IP where it creates differentiated value</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Keep human authority and evidence explicit</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Company Links */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Link to="/company/structure" className="group">
              <div className="card-pillar p-8 h-full">
                <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center mb-4">
                  <Building className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="text-title text-foreground mb-2 group-hover:text-foreground/80 transition-colors">
                  Our Companies
                </h3>
                <p className="text-body mb-4">
                  Group structure and operating divisions. Master brand and legal entities.
                </p>
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  View structure →
                </span>
              </div>
            </Link>
            <Link to="/company/intellectual-property" className="group">
              <div className="card-pillar p-8 h-full">
                <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center mb-4">
                  <Eye className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="text-title text-foreground mb-2 group-hover:text-foreground/80 transition-colors">
                  Intellectual Property
                </h3>
                <p className="text-body mb-4">
                  Patents, trademarks and registered designs. Protection status and scope.
                </p>
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  View IP portfolio →
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-headline text-foreground mb-8 text-center">
              Contact
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="card-premium p-6 text-center">
                <MapPin className="h-5 w-5 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Glasgow, Scotland</p>
              </div>
              <div className="card-premium p-6 text-center">
                <Mail className="h-5 w-5 text-muted-foreground mx-auto mb-3" />
                <a href="mailto:info@rlktech.co.uk" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  info@rlktech.co.uk
                </a>
              </div>
              <div className="card-premium p-6 text-center">
                <Phone className="h-5 w-5 text-muted-foreground mx-auto mb-3" />
                <a href="tel:+441413194813" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  0141 319 4813
                </a>
              </div>
            </div>
            <div className="text-center mt-8">
              <Link to="/contact" className="btn-primary">
                Get in Touch
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
